<template>
  <div class="container content-block" v-if="fetchingBuddy">
    <div class="row mt-3 mb-3">
      <div class="col text-center">
        <div class="alert alert-info" role="alert">
          Buddy ophalen.
        </div>
        <div class="d-flex justify-content-center">
          <div class="spinner-border" role="status">
            <span class="sr-only">Laden...</span>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="buddy == null">
    <NotFound></NotFound>
  </div>
  <div class="container" v-else>
    <div class="row">
      <ButtonBack></ButtonBack>
    </div>

    <div class="row agenda-cards mb-5">
      <div class="col-lg-4">
        <div class="buddy-and-next-activity">
          <BuddySelectButton :buddy="buddy" :showEditButton="isActiveBuddy" :interactable="isActiveBuddy"
                             @editClicked="editBuddyClicked"></BuddySelectButton>
          <AgendaNextActivity :next-activity="nextActivity" :buddyId="buddyId"
                              @startActivityClicked="startActivityClicked"
                              :showFinishYearButton="true"></AgendaNextActivity>

          <v-button v-if="buddy.name != null" class="mt-3" button-text="Diploma aanmaken" :enabled="true"
                    @click="diplomaClicked"></v-button>

          <div class="text-center mt-3">

            <button class="btn btn-link delete-buddy-dialog-btn" type="button" @click="showRemoveBuddyDialog = true">
              Buddy
              verwijderen
            </button>
            <v-modal v-if="showRemoveBuddyDialog" @close="showRemoveBuddyDialog = false">
              <template v-slot:header>
                <h3>Buddy verwijderen</h3>
              </template>
              <template v-slot:body>
                <p>
                  Ben je zeker dat je deze buddy wil verwijderen? Je kan dit niet ongedaan maken.
                </p>
              </template>
              <template v-slot:footer>
                <button class="btn delete-buddy-btn" @click="deleteBuddy">Buddy verwijderen</button>
                <button class="btn btn-secondary" @click="showRemoveBuddyDialog = false">Annuleer</button>
              </template>
            </v-modal>
          </div>
        </div>
      </div>

      <div class="col-lg-8">
        <div class="container content-block" v-if="fetchingActivities">
          <div class="row mt-3 mb-3">
            <div class="col text-center">
              <div class="alert alert-info" role="alert">
                Activiteiten ophalen.
              </div>
              <div class="d-flex justify-content-center">
                <div class="spinner-border" role="status">
                  <span class="sr-only">Laden...</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="container content-block" v-else-if="activities.length === 0">
          <div class="row mt-3 mb-3">
            <div class="col text-center">
              <div class="alert alert-warning" role="alert">
                Er zijn nog geen activiteiten gemaakt voor deze buddy.
              </div>
            </div>
          </div>
        </div>
        <div class="row" v-else>
          <div class="col-lg-6">
            <div class="card">
              <div class="total-activities">
                <p>Totaal activiteiten</p>
                <p>{{ completedActivities.length }}</p>
              </div>
              <div class="activities-bar">
                <div class="bar">
                  <div class="fill" :key="activityPercentage.id" v-for="activityPercentage in activityPercentages"
                       :style="activityPercentage"></div>
                </div>
              </div>
              <div class="activities">
                <QuestTypeLabel :key="questTypeStats.questType" v-for="questTypeStats in questTypeStatsList"
                                :quest-type="questTypeStats.questType" :amount="questTypeStats.amount"></QuestTypeLabel>
              </div>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="card">
              <div class="buddy-summary">
                <p class="title">Samenvatting</p>
                <div class="summary-item">
                  <p class="font-weight-bold">Eerste activiteit</p>
                  <p v-if="firstCompletedPlannedActivity !== null">
                    {{ formatDateMonthYear(firstCompletedPlannedActivity.completionDate) }}
                  </p>
                  <p v-else>/</p>
                </div>
                <div class="divider"></div>

                <div class="summary-item">
                  <p class="font-weight-bold">Laatste activiteit</p>
                  <p v-if="lastCompletedPlannedActivity !== null">
                    {{ formatDateMonthYear(lastCompletedPlannedActivity.completionDate) }}
                  </p>
                  <p v-else>/</p>
                </div>
                <div class="divider"></div>

                <div class="summary-item">
                  <p class="font-weight-bold">Volgende activiteit</p>
                  <p v-if="nextActivity !== null">{{ formatDateMonthYear(nextActivity.plannedDate) }}</p>
                  <p v-else>/</p>
                </div>
                <div class="divider"></div>

                <div class="summary-item">
                  <p class="font-weight-bold">Geplande activiteiten</p>
                  <p>{{ plannedActivities.length }}</p>
                </div>
              </div>

              <div class="logbook-wrapper mt-3" v-if="buddyIsInitialized">
                <button class="btn btn-sandpiper btn-block" type="button" @click="generateLogbook"
                        :disabled="generatingLogbook" v-if="!logbookGenerated">
                  Logbook genereren
                </button>
                <div class="buddy-logbook" @click="downloadLogbook" v-else>
                  <div class="logbook-icon">
                    <AttachmentIcon/>
                  </div>
                  <div class="logbook-filename">
                    {{ buddyLogbookFilename }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AgendaNextActivity from "@/components/Agenda/AgendaNextActivity";
import BuddySelectButton from "@/components/Common/BuddySelectButton";
import QuestTypeLabel from "@/components/Quests/QuestTypeLabel";
import ButtonBack from "@/components/UI/ButtonBack";


import DateFormatter from "@/mixins/DateFormatter";
import QuestTypeHelper from "@/mixins/QuestTypeHelper";
import log from "@/utils/logger";
import { BuddyApi, QuestTypeEnum } from "sandpiper_api";

import AttachmentIcon from "@/assets/image/icons/icon-attachment.svg?inline";
import NotFound from "@/components/Common/NotFound";
import VModal from "@/components/UI/VModal";
import VButton from "@/components/UI/VButton.vue";

function sleep(ms)
{
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default {
  name: "BuddyDetail",
  components: {
    VModal,
    AgendaNextActivity,
    BuddySelectButton,
    QuestTypeLabel,
    ButtonBack,
    AttachmentIcon,
    NotFound,
    VButton
  },
  mixins: [ DateFormatter, QuestTypeHelper ],
  inject: [ "doApiRequest" ],
  data()
  {
    return {
      buddyId: null,
      fetchingBuddy: false,
      deletingBuddy: false,
      buddy: {},
      activityData: {},
      fetchingActivities: false,
      activities: [],

      generatingLogbook: false,

      showRemoveBuddyDialog: false
    };
  },
  computed: {
    isActiveBuddy()
    {
      return this.buddy?.buddyConfig.schoolYear.endDate > new Date();
    },
    activityPercentages()
    {
      let percentages = [];
      this.questTypeStatsList.forEach(questTypeStats => {
        let percentageStyle = {
          "width": (questTypeStats.amount / this.completedActivities.length) * 100 + "%",
          "background-color": this.questTypeColor(questTypeStats.questType),
        }
        percentages.push(percentageStyle);
      });

      return percentages;
    },
    plannedActivities()
    {
      let now = new Date();
      return this.activities
                 .filter(a => a.plannedDate > now)
                 .sort((a1, a2) => a1.plannedDate - a2.plannedDate);
    },
    nextActivity()
    {
      return this.plannedActivities.length > 0 ? this.plannedActivities[0] : null;
    },
    questTypeStatsList()
    {
      const questTypeStats = [
        { questType: new QuestTypeEnum().more, amount: 0 },
        { questType: new QuestTypeEnum().impulse, amount: 0 },
        { questType: new QuestTypeEnum().smart, amount: 0 },
        { questType: new QuestTypeEnum().together, amount: 0 }
      ];

      if (this.completedActivities && this.completedActivities.length > 0) {

        this.completedActivities.forEach(act => {
          let questType = questTypeStats.find(t => t.questType === act.quest.type);
          if (questType) {
            questType.amount++;
          }
        })

        return questTypeStats;

        /*
        const activityQuestTypeCount = this.completedActivities.reduce((stats, activity) =>
        {
          let questType = activity.quest.type;
          if (!Object.prototype.hasOwnProperty.call(stats, questType))
          {
            stats[questType] = 0
          }
          stats[questType]++;

          console.log(stats);
          return stats;
        });


        questTypeStats.forEach(qts =>
        {
          if (Object.prototype.hasOwnProperty.call(activityQuestTypeCount, qts.questType))
          {
            qts.amount = activityQuestTypeCount[qts.questType];
          }
        });

         */
      }

      return questTypeStats;
    },
    completedActivities()
    {
      return this.activities.filter(a => a.completionDate !== null).sort((a1, a2) =>
      {
        const date1 = a1.completionDate !== null ? a1.completionDate : Infinity;
        const date2 = a2.completionDate !== null ? a2.completionDate : Infinity;
        return date1 - date2;
      });
    },
    firstCompletedPlannedActivity()
    {
      return this.completedActivities.length > 0 ? this.completedActivities[0] : null;
    },
    lastCompletedPlannedActivity()
    {
      return this.completedActivities.length > 0 ? this.completedActivities[this.completedActivities.length - 1] : null;
    },

    buddyIsInitialized()
    {
      return this.buddy?.name && this.buddy?.name.trim() !== "" && this.completedActivities.length > 0;
    },
    logbookGenerated()
    {
      return this.buddy.logbook && !this.buddy.logbook.requiresRerender && this.buddy.logbook.asset;
    },
    buddyLogbookFilename()
    {
      return "activiteiten_logbook.pdf";
    }
  },
  methods: {
    startActivityClicked()
    {
      this.$router.push({
        name: 'student-platform', params: {
          id: this.buddyId
        }
      });
    },
    diplomaClicked()
    {
      this.$router.push({
        name: 'buddy-diploma', params: {
          id: this.buddyId
        }
      });
    },
    editBuddyClicked(buddy)
    {
      log.debug("Clicked edit buddy");

      this.$router.push({
        name: 'edit-buddy', params: {
          id: buddy.id,
          initialClassName: buddy.buddyConfig.className,
          initialStudentCount: buddy.buddyConfig.studentCount,
          initialSchoolId: buddy.buddyConfig.school.id,
          initialBoys: buddy.buddyConfig.genders.boys,
          initialGirls: buddy.buddyConfig.genders.girls,
          initialOther: buddy.buddyConfig.genders.other,
          initialGrade: buddy.buddyConfig.grade,
        }
      });
    },
    fetchPathParams()
    {
      this.buddyId = this.$route.params.id;
    },
    async fetchBuddy()
    {
      try
      {
        log.info("Fetching buddy");
        this.fetchingBuddy = true;
        this.buddy = null;
        this.buddy = await this.doApiRequest(() => new BuddyApi().getBuddy(this.buddyId));
        this.buddy.selected = true;
        if (this.buddy.logbook)
        {
          this.buddy.logbook.requiresRerender = true;
        }
      }
      catch (e)
      {
        // TODO - handle error
        log.error("Failed to fetch buddy - %o", e);
      }
      finally
      {
        this.fetchingBuddy = false;
      }
    },
    async deleteBuddy()
    {
      if (this.deletingBuddy) return;

      try
      {
        log.info("Deleting buddy");
        this.deletingBuddy = true;
        await this.doApiRequest(() => new BuddyApi().deleteBuddy(this.buddyId));
        await this.$router.push({ name: "profile" });
      }
      catch (e)
      {
        // TODO - handle error
        log.error("Failed to delete buddy - %o", e);
      }
      finally
      {
        this.deletingBuddy = false;
      }
    },
    async fetchBuddyActivities()
    {
      try
      {
        this.fetchingActivities = true;
        this.activities = [];
        this.activities = await this.doApiRequest(() => new BuddyApi().getBuddyActivities(this.buddyId, {
          beginDate: null,
          endDate: null,
          questId: null
        }));

        // Sort by plannedDate
        this.activities.sort((a1, a2) => a1.plannedDate - a2.plannedDate);

        console.log(this.activities)
      }
      catch (e)
      {
        // TODO - handle error
      }
      finally
      {
        this.fetchingActivities = false;
      }
    },
    loadData()
    {
      this.fetchBuddy();
      this.fetchBuddyActivities();
    },

    async generateLogbook()
    {
      try
      {
        log.info("Generating logbook for buddy");
        this.generatingLogbook = true;
        await this.doApiRequest(() => new BuddyApi().generateLogbookForBuddy(this.buddyId));
        await this.pollFetchLogbook();
      }
      catch (e)
      {
        // TODO - handle error
        log.error("Failed to generate logbook - %o", e);
      }
    },
    async pollFetchLogbook()
    {
      let retries = 10;
      let logbook;

      while (retries-- > 0 && !logbook?.asset)
      {
        try
        {
          log.info("Fetching logbook for buddy");
          logbook = await this.doApiRequest(() => new BuddyApi().getLogbookForBuddy(this.buddyId));

          if (logbook?.asset)
          {
            break;
          }
        }
        catch (e)
        {
          log.error("Failed to fetch logbook for buddy - %o", e);
        }

        await sleep(5000);
      }

      this.generatingLogbook = false;

      if (logbook?.asset)
      {
        this.buddy.logbook = logbook;
      }
      else
      {
        // TODO - handle error
      }
    },
    downloadLogbook()
    {
      window.open(this.buddy.logbook.asset.url, "_blank");
    }
  },
  created()
  {
    this.fetchPathParams();
    this.loadData();
  }
}
</script>

<style lang="scss" scoped>
.delete-buddy-dialog-btn
{
  color: $error;
}

.delete-buddy-btn
{
  background-color: $error;
  color: #ffffff;
  border-color: $error;

  &:hover
  {
    background-color: darken($error, 7.5%);
    border-color: darken($error, 10%);
  }

  &:focus,
  .focus
  {
    background-color: darken($error, 7.5%);
    border-color: darken($error, 10%);
  }

  &.disabled,
  &:disabled
  {
    background-color: $error;
    border-color: $error;
  }

  &:not(:disabled):not(.disabled):active,
  &:not(:disabled):not(.disabled).active
  {
    background-color: darken($error, 10%);
    border-color: darken($error, 12.5%);
  }
}

.agenda-cards
{
  align-items: flex-start;
  margin-top: 25px;
}

.card
{
  background-color: white;
  border-radius: 5px;
  box-shadow: $default-shadow;
  padding: 25px;

  &.next-activity-card
  {
    margin-top: 25px;
  }
}

.summary-item
{
  display: flex;
  justify-content: space-between;
  font-size: $body-text-size;
  padding: 16px 0px;

  p
  {
    margin-bottom: 0px;
  }
}

.divider
{
  height: 2px;
  background-color: $font-grey;
}

.total-activities
{
  display: flex;
  justify-content: space-between;

  p:first-child
  {
    font-weight: 500;
  }
}

.activities-bar
{
  display: flex;
  align-items: center;
  height: 20px;
  margin-bottom: 1rem;

  .level
  {
    padding-right: 10px;
    font-weight: bold;
    color: $primary-blue;
  }

  .bar {
    flex: 5 5;
    display: flex;
    height: 10px;
    border-radius: 5px;
    overflow: hidden;

    .fill {
      background-color: $primary-blue;
      width: 100%;
      height: 100%;

      &:first-child {
        border-radius: 5px 0px 0px 5px;
      }

      &:last-child
      {
        border-radius: 0px 5px 5px 0px;
      }
    }
  }
}

.title
{
  font-size: $title-size;
  font-weight: bold;
  margin-bottom: 0.3rem;
}

.buddy-logbook
{
  display: flex;
  flex-direction: row;
  justify-content: center;

  border-radius: 5px;
  background-color: white;
  box-shadow: $default-shadow;
  margin-bottom: 0.66rem;

  cursor: pointer;

  .logbook-icon
  {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 10px;

    background-color: $primary-blue;

    min-width: 50px;
    font-size: 30px;

    border-top-left-radius: 5px;
    border-bottom-left-radius: 5px;

    svg
    {
      fill: $font-light;
    }
  }

  .logbook-filename
  {
    padding: 10px;
    flex: 1 1;

    display: flex;
    flex-direction: row;

    justify-content: flex-start;
    align-items: center;

    font-size: 0.875rem;
    font-weight: 500;

    min-width: 0; // To make the ellipsis work https://css-tricks.com/flexbox-truncated-text/
  }
}
</style>